/**
 * Command-bar density tiers (council Part IV / Part IX — the overflow fix).
 *
 * The bar spans the MAP REGION only, so its real budget is the region width —
 * NOT the viewport (rail 360 + detail panel 380 are both in-flow at ≥1536px,
 * so a 1920×1080 desk leaves the bar just ~1180px and a 1536px window only
 * ~796px). The old `2xl:` viewport gating got this backwards: a WIDER viewport
 * can mean a SMALLER map region. Tiers are therefore chosen from the bar's own
 * measured width (the #186/#211 measured-size idiom), and the ladder is
 * doctrine-led: low-frequency groups demote into the Settings ▾ overflow (the
 * #191 worked pattern) before any chrome squeezes, and nothing ever truncates
 * or wraps.
 *
 * Measured requirements (2026-06-11, real Chrome metrics; "worst" adds the
 * active-tool Cancel swell + transient button-text growth on top of the
 * always-counted Recent/Queue badges):
 *
 *   tier      on the bar                                  typical   worst
 *   full      captions + every group + search input        ~1473    ~1610
 *   compact   captions hidden (icon triggers)              ~1090    ~1230
 *   overflow  + News/Drone → Settings ▾, short Cancel       ~1000    ~1030
 *   minimal   + VIEW → Settings ▾, search → icon, gap-1.5    ~780     ~770
 *
 * Thresholds = worst-case requirement + safety margin. Canonical budgets they
 * must serve (rail 360 + panel 380 open): 2560→1820 full · 1920→1180 overflow ·
 * 1280→920 minimal · 1536→796 minimal (the tightest in-flow band).
 *
 * `commandBarTierForWidth(0)` (unmeasured — first frame, jsdom) returns 'full'
 * so tests and SSR render every control; the ResizeObserver corrects it before
 * the operator can interact.
 *
 * REGRESSION GUARD: CommandBar carries a DEV-mode tripwire that logs a console
 * error whenever the bar's content overflows its region — any future group
 * added without re-tuning these thresholds fails the "zero console errors"
 * browser-verify ritual instead of silently overlapping the detail panel.
 */

export type CommandBarTier = 'full' | 'compact' | 'overflow' | 'minimal';

/** Minimum bar (= map region) width for each tier, px. */
export const COMMAND_BAR_TIER_MIN_WIDTH: Record<Exclude<CommandBarTier, 'minimal'>, number> = {
  full: 1660,
  compact: 1280,
  overflow: 1060,
};

export function commandBarTierForWidth(regionWidth: number): CommandBarTier {
  if (regionWidth <= 0) return 'full'; // unmeasured — render everything
  if (regionWidth >= COMMAND_BAR_TIER_MIN_WIDTH.full) return 'full';
  if (regionWidth >= COMMAND_BAR_TIER_MIN_WIDTH.compact) return 'compact';
  if (regionWidth >= COMMAND_BAR_TIER_MIN_WIDTH.overflow) return 'overflow';
  return 'minimal';
}

/** Group captions (VIEW / REPORT / REQUEST / AUTHOR…) + button labels show only at full. */
export function showBarCaptions(tier: CommandBarTier): boolean {
  return tier === 'full';
}

/** News + Drone (ambient media pins — low-frequency, Part IX) demote into Settings ▾. */
export function demoteMediaMenus(tier: CommandBarTier): boolean {
  return tier === 'overflow' || tier === 'minimal';
}

/** The VIEW group (home / set-home / regions) demotes into Settings ▾ last. */
export function demoteViewGroup(tier: CommandBarTier): boolean {
  return tier === 'minimal';
}

/** The search input collapses to a magnifier button (popover hosts the same SearchBox). */
export function compactSearch(tier: CommandBarTier): boolean {
  return tier === 'minimal';
}

/** The active-tool Cancel button drops the tool-name suffix below compact. */
export function compactCancelLabel(tier: CommandBarTier): boolean {
  return tier === 'overflow' || tier === 'minimal';
}
